import { useRef, useState, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useInvalidate, useNotification, useShow, type HttpError } from "@refinedev/core";
import { Show, TextFieldComponent as TextField } from "@refinedev/mui";
import { axiosInstance } from "@/providers/data";
import { userDeleteNotifications } from "@/lib/account-deletion";
import { DeletionStatus } from "./deletion-status";

// Mirrors the server's `IMAGE_CONTENT_TYPES` multer filter; anything else is a
// 415 from the route, so there is no point offering it in the picker.
const PORTRAIT_ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * The user's account picture, with the admin's set / remove controls.
 *
 * Talks to `POST | DELETE /api/v2/user/:id/avatar`, the admin twin of the
 * player's own `/user/me/avatar` pair: same service behind it, so the upload
 * is normalized to webp, thumbnailed, moderated and stored in the TARGET
 * user's creator library exactly as a self-upload would be. The page shows
 * whatever `GET /user/:id` resolves as `avatarUrl` — the 512px thumb when
 * there is one — and re-reads the record after every change rather than
 * trusting the response, so the detail view and the list column can never
 * disagree about which picture a user has.
 *
 * "Likeness consent attested" is the same switch the web profile page has:
 * moderation refuses photographs of real people unless someone attests the
 * rights, and a portrait is usually exactly that. It is off by default so the
 * attestation is a deliberate act — the server stamps it into the asset's
 * metadata as a durable claim.
 */
function PortraitField({
  userId,
  avatarUrl,
  label,
}: {
  userId: number | string | undefined;
  avatarUrl: string | null | undefined;
  label: string;
}) {
  const invalidate = useInvalidate();
  const { open } = useNotification();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [likenessAttested, setLikenessAttested] = useState(false);

  const refresh = () =>
    invalidate({ resource: "user", invalidates: ["detail", "list"], id: userId });

  const notifyError = (error: unknown, fallback: string) => {
    const message =
      error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
        ? (error as { message: string }).message
        : fallback;
    open?.({ type: "error", message });
  };

  const upload = async (file: File) => {
    if (userId === undefined) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("likenessAttested", String(likenessAttested));
      await axiosInstance.post(`/api/v2/user/${userId}/avatar`, form);
      await refresh();
      open?.({ type: "success", message: "Portrait updated" });
    } catch (error) {
      // A moderation refusal (422) leaves the previous picture linked — the
      // server's message says why, and the page still shows the old one.
      notifyError(error, "Portrait upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (userId === undefined) return;
    setBusy(true);
    try {
      await axiosInstance.delete(`/api/v2/user/${userId}/avatar`);
      await refresh();
      open?.({ type: "success", message: "Portrait removed" });
    } catch (error) {
      notifyError(error, "Could not remove the portrait");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
      {avatarUrl ? (
        <Link href={avatarUrl} target="_blank" rel="noreferrer">
          <Avatar src={avatarUrl} alt={label} sx={{ width: 96, height: 96 }} />
        </Link>
      ) : (
        <Avatar alt={label} sx={{ width: 96, height: 96 }}>
          {label.trim().charAt(0).toUpperCase() || "?"}
        </Avatar>
      )}
      <Stack gap={1}>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            size="small"
            disabled={busy || userId === undefined}
            onClick={() => inputRef.current?.click()}
          >
            {avatarUrl ? "Replace portrait" : "Upload portrait"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            disabled={busy || !avatarUrl}
            onClick={() => void remove()}
          >
            Remove
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept={PORTRAIT_ACCEPT}
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Reset so picking the same file again after a refusal re-fires.
              event.target.value = "";
              if (file) void upload(file);
            }}
          />
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={likenessAttested}
              onChange={(event) => setLikenessAttested(event.target.checked)}
              disabled={busy}
            />
          }
          label={
            <Typography variant="body2">
              Likeness consent attested (lifts the real-person moderation block)
            </Typography>
          }
        />
      </Stack>
    </Stack>
  );
}

/** `GET /user/:id`'s `mature` (immersive-server `AdminUserMature`): the user's switch and the account's ceiling. */
interface UserMature {
  switch: boolean | null;
  allowed: boolean;
  allowedBecause: "dated_adult" | "platform_adult" | "confirmed" | "minor" | "withdrawn" | "no_date";
}

const ALLOWED_BECAUSE: Record<UserMature["allowedBecause"], string> = {
  dated_adult: "Yes — date of birth says 18+",
  platform_adult: "Yes — App Store / Google Play age signal says 18+ (no date of birth on file)",
  confirmed: "Yes — confirmed 18+ (no date of birth on file)",
  minor: "No — known minor",
  withdrawn: "No — withdrawn (consent turned off)",
  no_date: "No — no date of birth on file",
};

/** The switch as the user left it; mature shows only when it is on AND the account is allowed. */
function matureToggleText(mature: UserMature | undefined): string {
  if (!mature) return "—";
  if (mature.switch === null) return "Off (never set)";
  if (!mature.switch) return "Off";
  return mature.allowed ? "On" : "On, but not allowed — mature does not show";
}

/** One key/value row of the two-column layout: the label left, the value right (stacked on a narrow screen). */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <Typography variant="body1" fontWeight="bold">
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, mb: { xs: 1, sm: 0 } }}>{children}</Box>
    </>
  );
}

// How often the page re-reads the user while a deletion job is open.
const DELETION_REFRESH_MS = 20_000;

export const UserShow = () => {
  const { query } = useShow({
    queryOptions: {
      // Follow an open deletion until it finishes; a finished one ends in 404, and polling stops there (the last
      // data, still carrying its open deletion, outlives the 404 in react-query's cache).
      refetchInterval: (current) =>
        (current.state.error as HttpError | null)?.statusCode === 404
          ? false
          : current.state.data?.data?.deletion
            ? DELETION_REFRESH_MS
            : false,
      // A 404 is an answer (the account is gone), not a blip worth retrying.
      retry: (failureCount, error) => (error as HttpError | null)?.statusCode !== 404 && failureCount < 3,
    },
    errorNotification: (error) =>
      error?.statusCode === 404
        ? false
        : { type: "error", message: error?.message ?? "Could not load this user." },
  });
  const { data, isLoading, error } = query;

  // Checked before the record: after a failed refetch react-query keeps the
  // last data, which would still show the account and its "Deletion running".
  if ((error as HttpError | null)?.statusCode === 404) {
    return (
      <Show canEdit={false} canDelete={false}>
        <Alert severity="info">This account was deleted. Account Deletions keeps its job.</Alert>
      </Show>
    );
  }

  const record = data?.data;
  return (
    <Show isLoading={isLoading} deleteButtonProps={userDeleteNotifications}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
          columnGap: 3,
          rowGap: 1.5,
          alignItems: "baseline",
        }}
      >
        {/* Only while a deletion job is open: the account stays until it finishes. */}
        {record?.deletion && (
          <Field label="Deletion">
            <Stack direction="row" alignItems="center">
              <DeletionStatus deletion={record.deletion} />
            </Stack>
          </Field>
        )}

        <Field label="Portrait">
          <PortraitField
            userId={record?.id}
            avatarUrl={record?.avatarUrl}
            label={record?.name || record?.email || ""}
          />
        </Field>

        <Field label="ID">
          <TextField value={record?.id} />
        </Field>

        <Field label="Email">
          <TextField value={record?.email} />
        </Field>

        <Field label="Handle">
          {/* Null when the account never chose one; see the list column's note. */}
          <TextField value={record?.handle ? `@${record.handle}` : "—"} />
        </Field>

        <Field label="Credits">
          <TextField value={record?.credits} />
        </Field>

        <Field label="Mature toggle">
          <TextField value={matureToggleText(record?.mature as UserMature | undefined)} />
        </Field>

        <Field label="Mature allowed">
          <TextField
            value={record?.mature ? ALLOWED_BECAUSE[(record.mature as UserMature).allowedBecause] : "—"}
          />
        </Field>

        <Field label="Last played on">
          {/* Play signal only — see the list column's note. Both fields are null
              together, so one guard covers both. */}
          <TextField
            value={
              record?.lastPlayClient
                ? `${record.lastPlayClient}${record.lastPlayAt ? ` · ${new Date(record.lastPlayAt).toLocaleString()}` : ""}`
                : "—"
            }
          />
        </Field>

        <Field label="Created At">
          <TextField value={record?.createdAt} />
        </Field>

        <Field label="Updated At">
          <TextField value={record?.updatedAt} />
        </Field>
      </Box>
    </Show>
  );
};
